/* eslint-disable camelcase */
import { OptionType, SORT_ORDER, TableSortOption } from '@evinced-private/ui-common';

import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { QueryVariablesBuilder } from 'src/common/helpers/GQLRequestsHelper/QueryVariablesBuilder';
import {
	ConditionInt,
	Uniqueness_IssuesSortFields,
	Uniqueness_IssuesSortOrder,
	Uniqueness_IssuesTextSearchFields
} from 'src/common/types/graphql.generated';

import { GetUniqueIssuesQueryVariables } from '../UniqueIssues.generated';

const uniqueIssuesSortFieldsMap: Record<string, Uniqueness_IssuesSortFields> = {
	issueType: Uniqueness_IssuesSortFields.IssueType,
	severity: Uniqueness_IssuesSortFields.Severity,
	pageTitle: Uniqueness_IssuesSortFields.PageTitle,
	pageUrl: Uniqueness_IssuesSortFields.PageUrl,
	firstSeen: Uniqueness_IssuesSortFields.FirstSeen,
	lastSeen: Uniqueness_IssuesSortFields.LastSeen
};

const uniqueIssuesSortOrderMap: Record<SORT_ORDER, Uniqueness_IssuesSortOrder> = {
	[SORT_ORDER.ASC]: Uniqueness_IssuesSortOrder.Ascending,
	[SORT_ORDER.DESC]: Uniqueness_IssuesSortOrder.Descending
};

const buildDaysFilter = (dateRangeOptions?: OptionType[]): ConditionInt | undefined => {
	if (!dateRangeOptions || dateRangeOptions.length === 0) {
		return { value: [30] };
	}

	const days = Number(dateRangeOptions[0].value);
	if (Number.isNaN(days)) {
		return { value: [30] };
	}

	return { value: [days] };
};

export const buildUniqueIssuesVariables = (
	filters: IFilterOptions,
	page: number,
	sizePerPage: number,
	sort?: TableSortOption
): GetUniqueIssuesQueryVariables => {
	const variablesBuilder = new QueryVariablesBuilder<GetUniqueIssuesQueryVariables>({
		sortFieldsMap: uniqueIssuesSortFieldsMap,
		sortOrderMap: uniqueIssuesSortOrderMap
	});

	variablesBuilder.addPaginationParams(page, sizePerPage);

	const defaultSort: TableSortOption = {
		dataField: 'issueType',
		order: SORT_ORDER.DESC
	};
	variablesBuilder.addSortParam(sort || defaultSort);

	const daysFilter = buildDaysFilter(filters.dateRange);
	if (daysFilter) {
		variablesBuilder.addConditionParam('daysFilter', daysFilter.value);
	}

	variablesBuilder
		.addFilterParam('severityFilter', filters.severities)
		.addFilterParam('issueTypeFilter', filters.types)
		.addFilterParam('environmentFilter', filters.environment);

	if (!filters.unitId || filters.unitId.length === 0 || filters.unitId[0]?.value === '') {
		variablesBuilder.addConditionParam('businessUnitFilter', '');
	} else {
		variablesBuilder.addFilterParam('businessUnitFilter', filters.unitId);
	}

	const variables = variablesBuilder.getVariables();

	if (filters.searchValue) {
		const searchValue = filters.searchValue.toString().trim();
		if (searchValue) {
			variables.textSearch = {
				searchFields: [
					Uniqueness_IssuesTextSearchFields.IssueType,
					Uniqueness_IssuesTextSearchFields.PageUrl,
					Uniqueness_IssuesTextSearchFields.PageTitle
				],
				searchValues: [searchValue],
				insensitive: true
			};
		}
	}

	return variables;
};
